import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Search, Mail, Crown, Gift, UserCheck, Sparkles, ArrowLeft, GraduationCap, CalendarClock, Plus, Ban, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
}

function isNew(iso) {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) / 86400000 <= 7;
}

// Returns { label, tone, icon } describing a member's TRUE access state.
function accessOf(m) {
  const eff = m.effective_status;
  if (eff === "comped") return { label: "Comped", tone: "comp", icon: Sparkles };
  if (eff === "expired") return { label: "Expired", tone: "expired", icon: CalendarClock };
  if (eff === "free") return { label: "Free account", tone: "free", icon: UserCheck };
  // active
  if (m.coupon && !m.is_stripe) return { label: m.coupon, tone: "coupon", icon: Gift };
  return { label: (m.subscription_plan || "member") + " · paying", tone: "paying", icon: Crown };
}

const TONE = {
  paying: "bg-green-400/15 text-green-400 border-green-400/30",
  coupon: "bg-gold/15 text-gold border-gold/30",
  comp: "bg-purple-400/15 text-purple-300 border-purple-400/30",
  expired: "bg-red-400/15 text-red-300 border-red-400/30",
  free: "bg-white/10 text-white/50 border-white/15",
};

export default function MembersPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("real"); // real | paying | expired | free | never | test
  const [loading, setLoading] = useState(true);

  const load = () => {
    return api.get("/admin/members")
      .then(({ data }) => setData(data))
      .catch(() => setData({ members: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const members = data?.members || [];

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return members.filter((m) => {
      const synthetic = m.is_test || m.is_mock;
      if (filter === "test" ? !synthetic : synthetic) return false;
      if (filter === "paying" && !["active", "comped"].includes(m.effective_status)) return false;
      if (filter === "expired" && m.effective_status !== "expired") return false;
      if (filter === "free" && m.effective_status !== "free") return false;
      if (filter === "never" && m.ever_subscribed) return false;
      if (filter === "billing" && !m.billing_issue) return false;
      if (!term) return true;
      return (m.name || "").toLowerCase().includes(term) || (m.email || "").toLowerCase().includes(term);
    });
  }, [members, q, filter]);

  const newThisWeek = members.filter((m) => !m.is_test && !m.is_mock && isNew(m.created_at)).length;

  const billingCount = data?.billing_issue_real ?? 0;

  const FILTERS = [
    { id: "real", label: "All members" },
    { id: "paying", label: "Active" },
    ...(billingCount > 0 ? [{ id: "billing", label: `Payment failed (${billingCount})` }] : []),
    { id: "expired", label: "Expired" },
    { id: "free", label: "Free" },
    { id: "never", label: "Never subscribed" },
    { id: "test", label: "Test / Mock" },
  ];

  return (
    <div className="min-h-screen bg-charcoal text-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-gold mb-4" data-testid="members-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="flex items-center gap-3 mb-1">
          <Users className="w-6 h-6 text-gold" />
          <h1 className="font-display text-3xl">Your Members</h1>
        </div>
        <p className="text-white/50 text-sm mb-6">Everyone who's here — see who to welcome and how they joined.</p>

        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="card-surface rounded-md p-4" data-testid="stat-total">
            <div className="text-2xl font-display text-white">{data?.total_real ?? "—"}</div>
            <div className="label-mono text-white/40 text-[10px] mt-1">Real members</div>
          </div>
          <div className="card-surface rounded-md p-4" data-testid="stat-active">
            <div className="text-2xl font-display text-green-400">{data?.active_real ?? "—"}</div>
            <div className="label-mono text-white/40 text-[10px] mt-1">Active access</div>
          </div>
          <div className="card-surface rounded-md p-4" data-testid="stat-expired">
            <div className="text-2xl font-display text-red-300">{data?.expired_real ?? "—"}</div>
            <div className="label-mono text-white/40 text-[10px] mt-1">Expired</div>
          </div>
          <div className="card-surface rounded-md p-4" data-testid="stat-new">
            <div className="text-2xl font-display text-gold">{newThisWeek}</div>
            <div className="label-mono text-white/40 text-[10px] mt-1">New this week</div>
          </div>
        </div>

        <GrantCourseAccess />

        {billingCount > 0 && (
          <button
            onClick={() => setFilter("billing")}
            data-testid="billing-alert-banner"
            className="w-full flex items-center gap-3 card-surface rounded-md p-4 mb-5 border border-red-400/40 bg-red-400/5 text-left hover:border-red-400/70 transition-colors"
          >
            <AlertTriangle className="w-5 h-5 text-red-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-red-200">
                {billingCount} member{billingCount > 1 ? "s have" : " has"} a failed payment
              </div>
              <div className="text-xs text-white/50 mt-0.5">
                Their card was declined — they still have access during Stripe's retry window. Reach out before they lapse. Tap to view.
              </div>
            </div>
          </button>
        )}

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…"
              className="bg-charcoal border-white/15 pl-9" data-testid="members-search" />
          </div>
          <div className="flex gap-1.5">
            {FILTERS.map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`members-filter-${f.id}`}
                className={`label-mono text-[11px] px-3 py-2 rounded-sm border transition-colors ${filter === f.id ? "border-gold/50 text-gold bg-gold/5" : "border-white/10 text-white/50 hover:border-gold/30"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-white/40 text-sm py-10 text-center">Loading members…</p>
        ) : shown.length === 0 ? (
          <p className="text-white/40 text-sm py-10 text-center" data-testid="members-empty">No members match.</p>
        ) : (
          <div className="space-y-2" data-testid="members-list">
            {shown.map((m) => (
              <MemberRow key={m.user_id || m.email} m={m} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MemberRow({ m, onChanged }) {
  const [busy, setBusy] = useState(false);
  const a = accessOf(m);
  const Icon = a.icon;
  const synthetic = m.is_test || m.is_mock;
  const expDate = m.subscription_expires ? new Date(m.subscription_expires).toLocaleDateString() : null;

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      await onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally { setBusy(false); }
  };

  const extend = () => act(
    () => api.post("/admin/members/extend", { email: m.email, days: 30 }),
    `Extended ${m.name || m.email} by 30 days`);
  const endAccess = () => {
    if (!window.confirm(`Remove access for ${m.name || m.email}? They'll lose member perks until they subscribe again.`)) return;
    act(() => api.post("/admin/members/comp", { email: m.email, action: "revoke" }),
      `Access ended for ${m.name || m.email}`);
  };
  const comp = () => act(
    () => api.post("/admin/members/comp", { email: m.email, action: "grant" }),
    `${m.name || m.email} is now comped (lifetime)`);

  return (
    <div data-testid={`member-row-${m.email}`} className="card-surface rounded-md p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium truncate">{m.name || m.email}</span>
            {isNew(m.created_at) && !m.is_test && (
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-gold/20 text-gold" data-testid="member-new-badge">NEW</span>
            )}
            {m.is_mock && (
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-purple-400/15 text-purple-300" data-testid="member-mock-badge">MOCK</span>
            )}
            {m.is_test && (
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-300/15 text-amber-300">TEST</span>
            )}
            {m.billing_issue && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-red-400/15 text-red-300 border border-red-400/30" data-testid={`member-billing-badge-${m.email}`}>
                <AlertTriangle className="w-2.5 h-2.5" /> PAYMENT FAILED
              </span>
            )}
          </div>
          <div className="text-xs text-white/40 font-mono truncate">{m.email}</div>
          <div className="text-[10px] font-mono mt-1 text-white/30">
            Joined {timeAgo(m.created_at)}
            {expDate && m.effective_status === "expired" && (
              <span className="text-red-300"> · expired {expDate}</span>
            )}
            {expDate && (m.effective_status === "active") && (
              <span> · access until {expDate}</span>
            )}
            {m.effective_status === "comped" && (
              <span className="text-purple-300"> · lifetime access</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm border ${TONE[a.tone]}`} data-testid={`member-access-${m.email}`}>
            <Icon className="w-3 h-3" /> {a.label}
          </span>
          <a href={`mailto:${m.email}?subject=${encodeURIComponent("Welcome to Help Solve The Case!")}`}
            data-testid={`member-welcome-${m.email}`}
            className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-gold">
            <Mail className="w-3 h-3" /> Welcome
          </a>
        </div>
      </div>

      {!synthetic && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/5">
          <button onClick={extend} disabled={busy} data-testid={`member-extend-${m.email}`}
            className="inline-flex items-center gap-1 text-[11px] font-mono px-2.5 py-1.5 rounded-sm border border-green-400/30 text-green-400 hover:bg-green-400/10 disabled:opacity-40 transition-colors">
            <Plus className="w-3 h-3" /> Extend 30 days
          </button>
          {m.effective_status !== "comped" && (
            <button onClick={comp} disabled={busy} data-testid={`member-comp-${m.email}`}
              className="inline-flex items-center gap-1 text-[11px] font-mono px-2.5 py-1.5 rounded-sm border border-purple-400/30 text-purple-300 hover:bg-purple-400/10 disabled:opacity-40 transition-colors">
              <Sparkles className="w-3 h-3" /> Comp (lifetime)
            </button>
          )}
          {["active", "comped"].includes(m.effective_status) && (
            <button onClick={endAccess} disabled={busy} data-testid={`member-end-${m.email}`}
              className="inline-flex items-center gap-1 text-[11px] font-mono px-2.5 py-1.5 rounded-sm border border-red-400/30 text-red-300 hover:bg-red-400/10 disabled:opacity-40 transition-colors">
              <Ban className="w-3 h-3" /> End access now
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function GrantCourseAccess() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [weeks, setWeeks] = useState(7);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const grant = async () => {
    if (!email.trim()) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/academy/grant", { email: email.trim(), backdate_weeks: Number(weeks), access_days: 120 });
      setResult(data);
      toast.success(`Course access granted to ${data.email} — ${data.weeks_unlocked} weeks unlocked`);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not grant access");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-surface rounded-md p-4 mb-5 border border-gold/20" data-testid="grant-course-card">
      <button onClick={() => setOpen(!open)} data-testid="grant-course-toggle" className="w-full flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-display font-semibold text-white">
          <GraduationCap className="w-4 h-4 text-gold" /> Grant course access (demo / backdate)
        </span>
        <span className="text-white/40 text-xs">{open ? "Hide" : "Open"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-white/50">Give any account the Homicide Investigations Course now (before launch) and backdate it so the first weeks are already open — perfect for your test/demo account.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Account email (e.g. your test account)"
              className="bg-charcoal border-white/15 flex-1" data-testid="grant-email-input" />
            <div className="flex items-center gap-2 shrink-0">
              <CalendarClock className="w-4 h-4 text-white/40" />
              <select value={weeks} onChange={(e) => setWeeks(e.target.value)} data-testid="grant-weeks-select"
                className="bg-charcoal border border-white/15 rounded-sm text-sm text-white px-2 py-2">
                {[0,1,2,3,4,5,6,7,8,9,10,11,12].map((w) => <option key={w} value={w}>{w === 0 ? "No backdate (week 1 only)" : `Backdate ${w} week${w>1?"s":""} → ${Math.min(12,w+1)} weeks open`}</option>)}
              </select>
            </div>
            <Button onClick={grant} disabled={busy || !email.trim()} data-testid="grant-course-btn" className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm shrink-0">
              {busy ? "Granting…" : "Grant access"}
            </Button>
          </div>
          {result && (
            <p className="text-xs text-green-400 font-mono" data-testid="grant-result">
              ✓ {result.email} — {result.weeks_unlocked} weeks unlocked · access until {new Date(result.access_until).toLocaleDateString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

